/**
 * ApplyRendererSurface — the load-lifecycle host for the director-form renderer
 * on `/apply` (director-form-application-cutover spec, task 8.2).
 *
 * The shared {@link ApplicationFormContainer} already owns draft/submit/lock and
 * bound-write orchestration, but it has only `loading` / `error` states (and an
 * inline "not available yet" when its snapshot is null). The cutover requirements
 * add three behaviors on top of the shared container that it deliberately does
 * NOT implement, so they live here instead of mutating the shared renderer:
 *
 *   1. **Loading** — while the active Published_Form_Version load is in progress,
 *      a loading state is shown and NO empty or partial form is rendered
 *      (Req 2.2).
 *   2. **Not-available** — when `getActiveFormVersion` yields no published
 *      version, a not-available state with a human-readable message, zero
 *      Form_Pages / zero Form_Fields, and NO submit / save-draft control
 *      (Req 2.3).
 *   3. **Error + retry + 10s timeout** — if the load rejects OR does not
 *      complete within 10 seconds (a `Promise.race` against a timer), an error
 *      state with a retry control; activating retry re-attempts the load and
 *      re-displays the error on a repeated failure/timeout (Req 2.4, 2.5).
 *
 * On a successful load of a Published_Form_Version it consults the built-in
 * Division_Selection_Entry stage gate (member-onboarding-flow, task 9.2):
 *
 *   - CLOSED (intake window not open) → render NO Phase_1_Forms; defer to the
 *     §B window-gate redirect (Req 4.5).
 *   - DIVISION_ENTRY (window open, no division chosen) → render the built-in
 *     {@link DivisionSelectionEntry} (a hardcoded step, NOT a director-authored
 *     DIVISION bound field — Req 4.2) and NO Phase_1_Forms (Req 4.1, 4.3). On
 *     confirm of exactly one valid division (`validateDivisionEntryCompletion`)
 *     it commits `selectSeason` → `selectDivision` and advances to PHASE_1 (Req
 *     4.4, 5.2); a validation miss (Req 4.7) or a write failure (Req 4.6, 5.6)
 *     keeps the entry open with the selection preserved and an error shown.
 *   - PHASE_1 (window open, division selected) → delegate to the existing
 *     {@link ApplicationFormContainer} with the selected `divisionKey` (Req 2.1,
 *     4.4), which performs its own load + render of the structure and answers;
 *     this surface's load is purely the published-version gate the container
 *     lacks.
 *
 * Validates: Requirements 2.1, 2.2, 2.3, 2.4, 2.5;
 *   member-onboarding-flow Requirements 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 4.7, 5.2,
 *   5.3, 5.5, 5.6
 */

import * as React from 'react';

import { Button } from '@/components/ui/button';
import CrownLoader from '@/components/ui/CrownLoader';
import { applicationFormClient } from '@/lib/application-form/client';
import type { FormVersion, PageApprovalStatus } from '@/lib/application-form/client';
import { resolveActiveCompetitionId } from '@/lib/application-form/activeCompetition';
import { backCompatMapping } from '@/lib/divisionVisibility';
import { resolveDivisionPrefix } from '@/lib/titlePrefix';
import { useActiveIntakeSeasons } from '@/hooks/useActiveIntakeSeasons';
import { useActiveSeason } from '@/hooks/useActiveSeason';
import { useHeadshotUrl } from '@/hooks/useHeadshotUrl';
import { LegacyAvatar } from '@/components/ui/LegacyAvatar';
import { useActiveTenantId } from '@/context/AuthContext';
import { useApplicationDivisions } from '@/components/application-form/hooks/useApplicationDivisions';
import { useApplicationTitles } from '@/components/application-form/hooks/useApplicationTitles';
import { useApplyDocumentSlots } from '@/components/application-form/hooks/useApplyDocumentSlots';
import {
  validateDivisionEntryCompletion,
  type ApplicantType,
} from '@/components/application-form/renderer/pure';
import { useApplicantApplicationState } from '@/components/application-form/hooks/useApplicantApplicationState';

import {
  ApplicationFormContainer,
  type ApplicationFormContainerProps,
} from '@/components/application-form/renderer/ApplicationFormContainer';
import type { BoundControlAdapter } from '@/components/application-form/renderer';
import { DivisionSelectionEntry } from './DivisionSelectionEntry';
import { ApplicationStatusView } from './ApplicationStatusView';
import { DerivedSeasonLabel } from './DerivedSeasonLabel';
import {
  SeasonTransitionNotice,
  shouldShowSeasonTransitionNotice,
} from '@/components/applicant/SeasonTransitionNotice';

/** The active-version load must complete within this window or it is treated as failed (Req 2.4). */
const DEFAULT_LOAD_TIMEOUT_MS = 10_000;

/** Sentinel thrown by the timeout leg of the `Promise.race` (Req 2.4). */
const LOAD_TIMEOUT = Symbol('apply-renderer-load-timeout');

export interface ApplyRendererSurfaceProps
  extends Partial<Omit<ApplicationFormContainerProps, 'competitionId' | 'divisionKey'>> {
  /** The competition whose Published_Form_Version the Contestant completes. */
  competitionId: string;
  /** The Contestant's assigned Division key (`''` when none yet — the empty selection). */
  divisionKey: string;
  /**
   * The load-timeout window in milliseconds. Defaults to 10 seconds (Req 2.4);
   * overridable so tests can drive the timeout deterministically.
   */
  loadTimeoutMs?: number;
}

type SurfaceState =
  | { status: 'loading' }
  | { status: 'not-available' }
  | { status: 'error' }
  | { status: 'ready' };

/**
 * Race a promise against a timer that rejects with {@link LOAD_TIMEOUT} after
 * `ms` (Req 2.4). The timer is always cleared so a fast-resolving load leaves no
 * dangling handle.
 */
function loadWithTimeout(promise: Promise<FormVersion | null>, ms: number): Promise<FormVersion | null> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(LOAD_TIMEOUT), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * Whether a resolved `getActiveFormVersion` result is a renderable
 * Published_Form_Version. A null/absent result, or one with no captured
 * snapshot, means the competition has no published form (Req 2.3).
 */
function hasPublishedVersion(version: FormVersion | null | undefined): version is FormVersion {
  return version != null && version.snapshot != null;
}

export function ApplyRendererSurface({
  competitionId,
  divisionKey,
  loadTimeoutMs = DEFAULT_LOAD_TIMEOUT_MS,
  ...containerProps
}: ApplyRendererSurfaceProps): React.ReactElement {
  const [state, setState] = React.useState<SurfaceState>({ status: 'loading' });
  /** Bumped by the retry control to re-run the load effect (Req 2.5). */
  const [attempt, setAttempt] = React.useState(0);
  /**
   * The contestant's OWN per-page approval statuses, keyed by `formPageId`
   * (Req 21). Loaded from the self-scoped `getMyPageApprovals` read once the
   * published-version gate resolves; handed to {@link ApplicationFormContainer}
   * so an APPROVED approval-required page is rendered read-only AND shows its
   * "Approved" pill. This is the data the container's lock logic previously never
   * received on the contestant path (the bug this fixes).
   */
  const [loadedPageApprovals, setLoadedPageApprovals] = React.useState<
    Record<string, PageApprovalStatus>
  >({});

  // ---- Built-in Division_Selection_Entry gate (member-onboarding-flow) ------
  // The stage gate opens the Phase_1_Forms only once a division has been chosen
  // within an open intake window (Req 4.1, 4.3, 4.4, 4.5). It is a pure function
  // of two derived inputs:
  //
  //   • `intakeOpen` — whether THIS competition's season is intake-open now,
  //     read from the Active_Intake_Season set (the season whose seeded
  //     `competitionId` matches the `/apply?competitionId=` target). The clock
  //     is injected inside `useActiveIntakeSeasons` at the I/O boundary.
  //   • `selectedDivisionKey` — the member's Participation.divisionKey for the
  //     season (the `divisionKey` prop resolved by `ApplyHost`), overlaid by any
  //     division just committed in this session, or `null` when none.
  const { seasons: activeSeasons, status: intakeStatus } = useActiveIntakeSeasons();
  // `operativeYear` drives year-scoped reads; `activeSeason` is the resolved
  // ACTIVE Season (present whether or not its intake window is open) and carries
  // the `intakeWindow.closesAt` the season-gated rejection-closure notice reads.
  const { season: activeSeason, operativeYear } = useActiveSeason();
  // The viewer's active tenant scopes the competitionId derivation so this
  // gate's per-season match uses the SAME tenant binding the `competitionId`
  // prop was derived from (tenant-foundation Task 7). Absent → Idaho (back-compat).
  const activeTenantId = useActiveTenantId();

  // The Active_Intake_Season for this competition, matched through the SAME
  // seeded `resolveActiveCompetitionId` mapping the target was derived from so
  // the gate can never disagree with the id it was handed. `null` ⇒ no open
  // window for this competition.
  const matchingSeason = React.useMemo(
    () =>
      activeSeasons.find(
        (season) =>
          resolveActiveCompetitionId(season.competitionYear, activeTenantId) ===
          competitionId,
      ) ?? null,
    [activeSeasons, competitionId, activeTenantId],
  );
  const intakeOpen = matchingSeason !== null;
  // The season year drives both the lazy-Participation `selectSeason` commit and
  // the division registry load; fall back to the operative year when the target
  // season is not (yet) in the open set so the divisions hook always has a year.
  const competitionYear = matchingSeason?.competitionYear ?? operativeYear;

  // The season's configured, application-selectable divisions — the options the
  // built-in entry offers (Req 4.1). Also handed to the Phase-1 container so its
  // division-scoped rendering has the registry the surface previously lacked.
  const { divisions: activeDivisions } = useApplicationDivisions(competitionYear);

  /** The division the member has picked in the entry but not yet confirmed (`''` = none). */
  const [pickedDivisionKey, setPickedDivisionKey] = React.useState('');
  /** A division committed in THIS session; overlays the prop so the stage re-resolves to PHASE_1 (Req 4.4). */
  const [committedDivisionKey, setCommittedDivisionKey] = React.useState<string | null>(null);
  /** Whether the `selectSeason` → `selectDivision` commit is in flight. */
  const [committing, setCommitting] = React.useState(false);
  /** The entry's user-facing error: the exactly-one miss (Req 4.7) or the write-failure "not saved" (Req 4.6). */
  const [entryError, setEntryError] = React.useState<string | null>(null);

  // The selected division for the stage decision: a just-committed key wins,
  // else the resolved Participation.divisionKey prop, else `null` (Req 4.3).
  const selectedDivisionKey =
    committedDivisionKey ?? (divisionKey && divisionKey.trim() !== '' ? divisionKey : null);

  // ---- Document slots: the document half of the bound-control adapter -------
  // Resolve the applicant's `applicantType` from their participation: the
  // selected Division key IS the participation's division, and the legacy
  // `applicantType` enum maps 1:1 onto the `mrs`/`miss` Division keys through the
  // SAME `backCompatMapping` the enum migration seeds — so we reverse it rather
  // than reimplement the mapping. A non-legacy division (e.g. `teen`) or the
  // empty selection yields `undefined`, which the catalog's applicant-type scope
  // and the container's document gate both treat as "no applicant type yet".
  const applicantType = React.useMemo<ApplicantType | undefined>(() => {
    if (!selectedDivisionKey) return undefined;
    return (Object.keys(backCompatMapping) as ApplicantType[]).find(
      (type) => backCompatMapping[type] === selectedDivisionKey,
    );
  }, [selectedDivisionKey]);

  // Load the per-competition Document_Catalog + the caller's own uploaded
  // documents and produce the adapter's document read-models + upload/remove
  // write callbacks (task 5.1). Merged into `boundControls` below so the
  // container's existing `computeMissingDocuments()` gates on real data.
  //
  // CATALOG-SOURCED SLOTS ONLY (apply-document-uploads Req 12.1; task 7.1): the
  // document slots this surface presents come EXCLUSIVELY from the per-competition
  // Document_Catalog. `useApplyDocumentSlots` loads `getDocumentCatalog(...)` and
  // the DOCUMENT_SLOT bound control renders `entriesInScope(catalog, divisionKey,
  // applicantType)` — there is NO hard-coded `DocumentType` list anywhere on the
  // `/apply` path. The legacy `ParticipantDocuments` surface still presents a
  // hard-coded list and COEXISTS with this surface: both write `DocumentReference`
  // rows to the SAME store and SAME shared S3 bucket (Req 12.2), and legacy
  // references without a `catalogEntryId` resolve without erroring the gate
  // (Req 12.3). Retiring `ParticipantDocuments` is a tracked follow-up.
  const documentSlots = useApplyDocumentSlots(competitionId, competitionYear, applicantType);

  // The applicant's own most recent HEADSHOT (this season's document slots
  // already cover it, no new fetch) — shown at the top of the application so
  // a mismatch between it and their account profile picture is visible to
  // the applicant themselves, not just a director reviewing later. No display
  // name is threaded in for the initials fallback (none is available in this
  // surface today) — LegacyAvatar degrades a blank name to "?", acceptable
  // since the photo itself, not the fallback, is the point.
  const headshotUrl = useHeadshotUrl(documentSlots.uploadedDocuments);

  // The active, in-scope season Title_Catalog the contestant selects their
  // ranked Title_Preferences from (season-scoped-title-registry Req 5.1). It is
  // resolved STRICTLY by THIS season's `competitionYear` and the contestant's
  // applicant scope, so a new season presents only its own catalog and never
  // carries over a prior season's titles or preferences (Req 5.4). When the
  // catalog is empty (no catalog seeded yet / off) the Title control falls back
  // to free-text entry, so this wiring is inert until a season has titles.
  const { options: titleOptions } = useApplicationTitles(
    competitionYear,
    applicantType,
    selectedDivisionKey ?? undefined,
  );

  // The bound-control adapter handed to the Phase-1 container: the incoming
  // `boundControls` (Division/Season/Title/contact) overlaid with the document
  // half from `useApplyDocumentSlots`. The transport catalog/reference DTOs type
  // `applicantTypeScope`/`catalogEntryId` wider (`string` / nullable) than the
  // pure adapter unions, so — exactly like the `ApplicationFormDevRoute` harness
  // — a single localized assertion bridges the client → pure document shapes
  // (and wires `onRemoveDocument`, the member task 5.3 adds to the adapter).
  // The contestant's current division, used to resolve the Division_Title_Prefix
  // shown greyed to the left of each free-text Title_Preferences row
  // (title-approval-tracking R3, R4). Resolved via `resolveDivisionPrefix`
  // (explicit `titlePrefix` else the division label); `undefined` when no
  // division is selected yet, so the control renders a bare input.
  const currentDivision = activeDivisions.find((d) => d.key === selectedDivisionKey);
  const titlePrefix = currentDivision ? resolveDivisionPrefix(currentDivision) : undefined;

  const boundControls = React.useMemo<BoundControlAdapter>(
    () =>
      ({
        ...containerProps.boundControls,
        documentCatalog: documentSlots.documentCatalog,
        applicantType: documentSlots.applicantType,
        uploadedDocuments: documentSlots.uploadedDocuments,
        onUploadDocument: documentSlots.onUploadDocument,
        onRemoveDocument: documentSlots.onRemoveDocument,
        // Catalog-backed title selection: an explicit host-supplied value still
        // wins; otherwise present this season's active, in-scope catalog.
        availableTitles: containerProps.boundControls?.availableTitles ?? titleOptions,
        // The resolved Division_Title_Prefix for the contestant's active division;
        // the free-text Title control shows it greyed and composes/strips it
        // around the stored Composed_Title (title-approval-tracking R3, R4).
        titlePrefix,
      }) as BoundControlAdapter,
    [containerProps.boundControls, documentSlots, titleOptions, titlePrefix],
  );

  // ---- Single authoritative applicant Application State ---------------------
  // The submission-aware resolver (shared with the dashboard) decides whether
  // the applicant sees the editable form, the division picker, an informational
  // panel, or the read-only status view — so a SUBMITTED/APPROVED/REJECTED
  // application is never re-shown as an editable form (Req 6, 7, 10).
  const appState = useApplicantApplicationState({
    competitionId,
    intakeOpen,
    // The surface's own load lifecycle already establishes whether a published
    // Form_Version exists; feed that in so the hook owns exactly one read (the
    // applicant's response) and never double-loads the form.
    hasPublishedForm: state.status === 'ready',
    selectedDivisionKey,
  });

  // Confirm the picked division: validate exactly-one-selectable, then commit
  // `selectSeason` (lazy Participation create/reuse) followed by `selectDivision`
  // (Participation.divisionKey), advancing to PHASE_1 on success (Req 4.4, 5.2).
  const handleConfirmDivision = React.useCallback(() => {
    const selection = pickedDivisionKey === '' ? [] : [pickedDivisionKey];
    const completion = validateDivisionEntryCompletion(selection, activeDivisions);
    if (!completion.ok) {
      // A validation miss stays in DIVISION_ENTRY and renders no Phase_1_Forms
      // (Req 4.7). The message is the pure core's user-facing explanation.
      setEntryError(completion.message);
      return;
    }

    setEntryError(null);
    setCommitting(true);
    void (async () => {
      try {
        // selectSeason first (creates/reuses the Participation for the season),
        // then selectDivision (writes Participation.divisionKey) — Req 5.2.
        await applicationFormClient.selectSeason(competitionId, competitionYear);
        await applicationFormClient.selectDivision(competitionId, completion.divisionKey);
        // Re-resolve the stage → PHASE_1 (Req 4.4).
        setCommittedDivisionKey(completion.divisionKey);
      } catch {
        // Preserve the selection, surface the "not saved" error, allow retry —
        // the confirm control re-enables when `committing` clears (Req 4.6, 5.6).
        setEntryError('Your division selection was not saved. Please try again.');
      } finally {
        setCommitting(false);
      }
    })();
  }, [pickedDivisionKey, activeDivisions, competitionId, competitionYear]);

  React.useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    loadWithTimeout(
      // The transport type is non-null, but the backend returns null when the
      // competition has no published form; both are handled below.
      applicationFormClient.getActiveFormVersion(competitionId, divisionKey) as Promise<
        FormVersion | null
      >,
      loadTimeoutMs,
    )
      .then((version) => {
        if (cancelled) return;
        setState(hasPublishedVersion(version) ? { status: 'ready' } : { status: 'not-available' });
      })
      .catch(() => {
        // Both a rejected load and the 10s timeout land here (Req 2.4).
        if (cancelled) return;
        setState({ status: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, [competitionId, divisionKey, loadTimeoutMs, attempt]);

  // ---- Load the contestant's own per-page approvals (Req 21) ----------------
  // Once the published-version gate resolves (`ready`), fetch the caller's OWN
  // page approvals via the self-scoped `getMyPageApprovals` read and reduce them
  // to a `formPageId → status` map. Handed to the container below so its
  // existing lock logic (`isPageEditableByApplicant`) finally receives the data
  // it needs, and so an APPROVED approval-required page shows its "Approved"
  // pill. Non-fatal: a failure leaves the map empty (pages default to editable),
  // so the surface never blocks on this secondary read.
  React.useEffect(() => {
    if (state.status !== 'ready') return;
    let cancelled = false;
    applicationFormClient
      .getMyPageApprovals(competitionId)
      .then((approvals) => {
        if (cancelled) return;
        const map: Record<string, PageApprovalStatus> = {};
        for (const approval of approvals) {
          map[approval.formPageId] = approval.status;
        }
        setLoadedPageApprovals(map);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadedPageApprovals({});
      });
    return () => {
      cancelled = true;
    };
  }, [competitionId, state.status, attempt]);

  const handleRetry = React.useCallback(() => {
    setAttempt((n) => n + 1);
  }, []);

  // ---- Loading: no empty/partial form during the load (Req 2.2, 11.1) ------
  // Hold loading until the form load, the Active_Intake_Season read, AND the
  // applicant-state read all resolve, so the surface never briefly renders the
  // wrong state before we know the window + the applicant's Application State.
  if (state.status === 'loading' || intakeStatus === 'loading' || appState.loading) {
    return (
      <div data-testid="apply-renderer-loading">
        <CrownLoader message="Loading your application…" />
      </div>
    );
  }

  // ---- Error + retry (form load rejected/timed out OR state read failed) ----
  if (state.status === 'error' || appState.error) {
    return (
      <div
        role="alert"
        data-testid="apply-renderer-error"
        className="space-y-4 rounded-card border border-destructive/40 bg-destructive-muted p-6 text-center shadow-subtle"
      >
        <p className="font-display text-body font-semibold text-destructive-text">
          We couldn’t load your application.
        </p>
        <p className="font-sans text-body-sm text-destructive-text">
          Something went wrong while loading the application. Please try again.
        </p>
        <div className="flex justify-center">
          <Button
            type="button"
            onClick={() => {
              handleRetry();
              appState.refetch();
            }}
            data-testid="apply-renderer-retry"
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const kind = appState.state.kind;

  // ---- Not-available: no published form for this competition (Req 8.3) ------
  if (state.status === 'not-available' || kind === 'NOT_AVAILABLE') {
    return (
      <div
        role="status"
        data-testid="apply-renderer-not-available"
        className="space-y-3 rounded-card border border-border bg-card p-8 text-center shadow-subtle"
      >
        <p className="font-display text-body font-semibold text-foreground">
          The application isn’t available yet.
        </p>
        <p className="font-sans text-body-sm text-muted-foreground">
          There’s no published application form for this competition right now. Please check back
          later.
        </p>
      </div>
    );
  }

  // ---- CLOSED: no open intake window and nothing started (Req 8.1, 8.2) -----
  if (kind === 'CLOSED') {
    return (
      <div
        role="status"
        data-testid="apply-renderer-closed"
        className="space-y-3 rounded-card border border-border bg-card p-8 text-center shadow-subtle"
      >
        <p className="font-display text-body font-semibold text-foreground">
          Applications aren’t open right now.
        </p>
        <p className="font-sans text-body-sm text-muted-foreground">
          There’s no open intake window for this competition. Please check back later.
        </p>
      </div>
    );
  }

  // ---- Read-only post-submission states → the status view (Req 6, 7) --------
  // A SUBMITTED application (awaiting review) and a REJECTED application
  // (terminal) are NEVER re-shown as an editable form; they render the read-only
  // ApplicationStatusView. APPROVED is NOT read-only: it unlocks Phase_2 pages
  // for the contestant to continue (application-form-builder Req 16), so it
  // routes to the editable container below.
  if (kind === 'SUBMITTED' || kind === 'REJECTED') {
    // Season-gated rejection closure (application-decision-feedback R6.2): a
    // REJECTED contestant sees the "we regret / reapply next season" closure
    // notice while the current season's intake remains closed, and reverts to
    // the ordinary status view once the next intake opens. The decision is the
    // SAME shared `shouldShowSeasonTransitionNotice` gate the ApplicantDashboard
    // uses, so the profile area and the dashboard can never disagree (R6.1–R6.3).
    if (
      kind === 'REJECTED' &&
      shouldShowSeasonTransitionNotice({
        season: activeSeason,
        submissionStatus: 'REJECTED',
        now: new Date(),
      })
    ) {
      return <SeasonTransitionNotice season={activeSeason} submissionStatus="REJECTED" />;
    }

    const decidedDivisionKey =
      'divisionKey' in appState.state ? appState.state.divisionKey : selectedDivisionKey ?? '';
    const divisionLabel =
      activeDivisions.find((d) => d.key === decidedDivisionKey)?.label ?? null;
    return (
      <ApplicationStatusView
        status={kind}
        divisionLabel={divisionLabel}
        titlePreferences={appState.response?.titlePreferences ?? undefined}
        submittedAt={appState.response?.submittedAt ?? undefined}
      />
    );
  }

  // ---- Editable states -------------------------------------------------------
  // DIVISION_ENTRY / NOT_STARTED → the built-in division picker and NO Phase-1
  // forms (Req 3.1). IN_PROGRESS / CHANGES_REQUESTED → the editable container,
  // division-scoped (Req 4). CHANGES_REQUESTED additionally surfaces the
  // director's return note above the form (Req 7.2).
  // The division the editable container renders against. Falls back to the
  // division carried on the applicant's own response (resolved from Participation
  // server-side) so a returning/post-approval contestant — whose in-session pick
  // is gone and whose legacy AYP-derived prop is empty — still renders their
  // division-scoped (and all-divisions) pages instead of an empty form.
  const responseDivisionKey =
    appState.response?.divisionKey && appState.response.divisionKey.trim() !== ''
      ? appState.response.divisionKey
      : null;
  const editableDivisionKey = selectedDivisionKey ?? responseDivisionKey ?? '';
  const stageContent =
    kind === 'DIVISION_ENTRY' || kind === 'NOT_STARTED' ? (
      <DivisionSelectionEntry
        activeDivisions={activeDivisions}
        selectedKey={pickedDivisionKey}
        onSelectedKeyChange={(key) => {
          setPickedDivisionKey(key);
          // A fresh pick clears any prior validation/"not saved" error.
          setEntryError(null);
        }}
        onConfirm={handleConfirmDivision}
        submitting={committing}
        error={entryError}
      />
    ) : (
      <>
        {kind === 'CHANGES_REQUESTED' ? (
          <div
            role="alert"
            data-testid="apply-renderer-changes-requested"
            className="rounded-card border border-warning/40 bg-warning-muted p-4 text-body-sm text-warning-text"
          >
            {/* The director's persisted Return_Feedback_Note is shown verbatim
                when present (application-decision-feedback Req 2.4, 2.5); when the
                app was returned without a note, fall back to the generic prompt. */}
            {appState.response?.returnFeedbackNote &&
            appState.response.returnFeedbackNote.trim() !== '' ? (
              <p className="whitespace-pre-wrap">{appState.response.returnFeedbackNote}</p>
            ) : (
              <>
                The pageant team has asked for a few changes before your application can be
                reviewed. Please update your answers and re-submit.
              </>
            )}
          </div>
        ) : null}
        <ApplicationFormContainer
          competitionId={competitionId}
          divisionKey={editableDivisionKey}
          activeDivisions={containerProps.activeDivisions ?? activeDivisions}
          boundControls={boundControls}
          fileUploadAdapter={containerProps.fileUploadAdapter}
          // The loaded, self-scoped page-approval map drives the container's page
          // lock + the "Approved" pill; an explicit host override still wins.
          pageApprovals={containerProps.pageApprovals ?? loadedPageApprovals}
          immutableFieldIds={containerProps.immutableFieldIds}
          // The Season's phase deadlines + label so the container can display the
          // Phase_1_Close_Date / Phase_2_Due_Date and render the specific
          // phase-closed / locked message (season-phase-deadlines Req 8, 9, 13).
          // The Phase_1_Close_Date IS the Season's intake window `closesAt`.
          phaseOneCloseAt={containerProps.phaseOneCloseAt ?? matchingSeason?.intakeWindow?.closesAt ?? null}
          phaseTwoDueDate={containerProps.phaseTwoDueDate ?? matchingSeason?.phaseTwoDueDate ?? null}
          seasonLabel={
            containerProps.seasonLabel ??
            (matchingSeason ? `${matchingSeason.competitionYear} Season` : null)
          }
          onSavedDraft={containerProps.onSavedDraft}
          onSubmitted={(resp) => {
            containerProps.onSubmitted?.(resp);
            // Re-resolve so the surface flips to the read-only status view
            // without a full reload (Req 5.3, 6.1).
            appState.refetch();
          }}
        />
      </>
    );

  // Read-only Derived_Application_Season context for the applicant (Option A —
  // R5.3). It is a plain, non-interactive label, never a season selection.
  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-4">
        <LegacyAvatar
          directUrl={headshotUrl}
          fullName=""
          size="md"
          variant="contestant"
        />
        <div className="space-y-1">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Profile</p>
          <h1 className="font-display text-3xl text-foreground">Your application</h1>
        </div>
      </header>
      <DerivedSeasonLabel season={matchingSeason} />
      {stageContent}
    </div>
  );
}

export default ApplyRendererSurface;
